// Module Documents - Interface Candidat
import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { DocumentChecklist } from '@/components/documents/DocumentChecklist';
import { DOCUMENTS_BY_SECTION, SECTION_LABELS } from '@/config/documentsBySection';
import {
  FiFolder, FiFile, FiUpload, FiDownload, FiTrash2,
  FiCheck, FiX, FiClock, FiAlertCircle, FiEye, FiUser,
  FiUsers, FiBriefcase, FiDollarSign, FiShield, FiTrendingUp, FiFileText
} from 'react-icons/fi';

// Types
interface Document {
  id: string;
  name: string;
  type: string;
  section: string; // Section du formulaire (personal_info, income_info, etc.)
  documentType: string; // Type spécifique du document (id-card, payslips, etc.)
  size: string;
  uploadDate: string;
  status: 'verified' | 'pending' | 'rejected';
  rejectReason?: string;
}

// Styled Components
const PageHeader = styled.div`
  margin-bottom: ${theme.spacing.xl};
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const PageSubtitle = styled.p`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text}80;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const StatCard = styled.div`
  background: white;
  padding: ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
`;

const StatValue = styled.div`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
`;

const UploadSection = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.xl};
`;

const UploadArea = styled.div`
  border: 2px dashed ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing['2xl']};
  text-align: center;
  cursor: pointer;
  transition: all ${theme.transitions.normal};

  &:hover {
    border-color: ${theme.colors.primary};
    background: ${theme.colors.primary}05;
  }
`;

const UploadIcon = styled.div`
  font-size: 48px;
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};
`;

const UploadText = styled.div`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const UploadHint = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
`;

const HiddenInput = styled.input`
  display: none;
`;

const CategorySection = styled.div`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.xl};
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.surface};
`;

const CategoryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary}15;
  color: ${theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const CategoryInfo = styled.div`
  flex: 1;
`;

const CategoryTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const CategoryCount = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}60;
`;

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const DocumentCard = styled.div`
  border: 1px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  transition: all ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.sm};
  }
`;

const DocIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.surface};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: ${theme.colors.primary};
  flex-shrink: 0;
`;

const DocInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocName = styled.div`
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DocMeta = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  white-space: nowrap;

  ${props => {
    switch (props.status) {
      case 'verified':
        return `
          background: ${theme.colors.success}20;
          color: ${theme.colors.success};
        `;
      case 'rejected':
        return `
          background: ${theme.colors.error}20;
          color: ${theme.colors.error};
        `;
      default:
        return `
          background: ${theme.colors.warning}20;
          color: ${theme.colors.warning};
        `;
    }
  }}
`;

const DocActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primary};
    color: white;
    transform: translateY(-2px);
  }
`;

const DeleteButton = styled(IconButton)`
  &:hover {
    background: ${theme.colors.error};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing['2xl']};
  color: ${theme.colors.text}60;
`;

export const CandidateDocuments: React.FC = () => {
  const [profileType] = useState<'employee' | 'entrepreneur' | 'informal'>('employee'); // À récupérer du profil utilisateur
  const [documents, setDocuments] = useState<Document[]>([
    {
      id: '1',
      name: 'Carte d\'identité.pdf',
      type: 'PDF',
      section: 'personal_info',
      documentType: 'id-card',
      size: '2.3 MB',
      uploadDate: '2024-01-15',
      status: 'verified'
    },
    {
      id: '2',
      name: 'Fiche de paie - Janvier 2024.pdf',
      type: 'PDF',
      section: 'income_info',
      documentType: 'payslips',
      size: '1.8 MB',
      uploadDate: '2024-01-20',
      status: 'pending'
    },
    {
      id: '3',
      name: 'Justificatif de domicile.pdf',
      type: 'PDF',
      section: 'personal_info',
      documentType: 'proof-address',
      size: '1.2 MB',
      uploadDate: '2024-01-18',
      status: 'rejected',
      rejectReason: 'Document illisible, veuillez télécharger une version de meilleure qualité'
    },
    {
      id: '4',
      name: 'Contrat de travail.pdf',
      type: 'PDF',
      section: 'employment_info',
      documentType: 'employment-contract',
      size: '3.5 MB',
      uploadDate: '2024-01-22',
      status: 'verified'
    }
  ]);

  const getIconForSection = (iconName: string) => {
    const icons: Record<string, React.ReactNode> = {
      'FiUser': <FiUser />,
      'FiUsers': <FiUsers />,
      'FiBriefcase': <FiBriefcase />,
      'FiDollarSign': <FiDollarSign />,
      'FiShield': <FiShield />,
      'FiTrendingUp': <FiTrendingUp />,
      'FiFileText': <FiFileText />,
      'FiFile': <FiFile />
    };
    return icons[iconName] || <FiFile />;
  };

  // Récupérer les sections selon le profil
  const sectionsConfig = DOCUMENTS_BY_SECTION[profileType] || {};
  const sections = Object.keys(sectionsConfig).map(sectionKey => {
    const label = SECTION_LABELS[sectionKey];
    return {
      id: sectionKey,
      name: label?.title || sectionKey,
      description: label?.description || '',
      icon: getIconForSection(label?.icon || 'FiFile'),
      documents: (sectionsConfig as any)[sectionKey]
    };
  });

  const getDocumentsBySection = (sectionId: string) => {
    return documents.filter(doc => doc.section === sectionId);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <FiCheck />;
      case 'rejected':
        return <FiX />;
      default:
        return <FiClock />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'verified':
        return 'Vérifié';
      case 'rejected':
        return 'Rejeté';
      default:
        return 'En attente';
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    // TODO: Implement file upload
    console.log('Uploading files:', e.target.files);
  };

  const handleDeleteDocument = (id: string) => {
    setDocuments(documents.filter(doc => doc.id !== id));
  };

  const stats = {
    total: documents.length,
    verified: documents.filter(d => d.status === 'verified').length,
    pending: documents.filter(d => d.status === 'pending').length,
    rejected: documents.filter(d => d.status === 'rejected').length
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <PageTitle>Mes Documents</PageTitle>
        <PageSubtitle>
          Téléchargez et gérez les documents nécessaires pour votre candidature
        </PageSubtitle>
      </PageHeader>

      <DocumentChecklist
        profileType={(profileType === 'employee' ? 'salarie' : profileType === 'informal' ? 'informel' : profileType) as any}
        documents={documents}
        onUploadClick={(section) => {
          // Scroll to the section
          const sectionElement = document.getElementById(`section-${section}`);
          if (sectionElement) {
            sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          // Trigger file upload
          document.getElementById('file-upload')?.click();
        }}
      />

      <StatsGrid>
        <StatCard>
          <StatValue>{stats.total}</StatValue>
          <StatLabel>Documents total</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.verified}</StatValue>
          <StatLabel>Vérifiés</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.pending}</StatValue>
          <StatLabel>En attente</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats.rejected}</StatValue>
          <StatLabel>Rejetés</StatLabel>
        </StatCard>
      </StatsGrid>

      <UploadSection>
        <HiddenInput
          type="file"
          id="file-upload"
          multiple
          onChange={handleFileUpload}
          accept=".pdf,.jpg,.jpeg,.png"
        />
        <UploadArea onClick={() => document.getElementById('file-upload')?.click()}>
          <UploadIcon><FiUpload /></UploadIcon>
          <UploadText>Cliquez pour télécharger des documents</UploadText>
          <UploadHint>Formats acceptés : PDF, JPG, PNG (max 10 MB)</UploadHint>
        </UploadArea>
      </UploadSection>

      {sections.map(section => {
        const sectionDocs = getDocumentsBySection(section.id);
        return (
          <CategorySection key={section.id} id={`section-${section.id}`}>
            <CategoryHeader>
              <CategoryIcon>{section.icon}</CategoryIcon>
              <CategoryInfo>
                <CategoryTitle>{section.name}</CategoryTitle>
                <div style={{ fontSize: theme.typography.sizes.sm, color: theme.colors.text + '80', marginBottom: theme.spacing.xs }}>
                  {section.description}
                </div>
                <CategoryCount>{sectionDocs.length} document(s) uploadé(s) / {section.documents.length} requis</CategoryCount>
              </CategoryInfo>
            </CategoryHeader>

            {sectionDocs.length > 0 ? (
              <DocumentList>
                {sectionDocs.map(doc => (
                  <DocumentCard key={doc.id}>
                    <DocIcon><FiFile /></DocIcon>
                    <DocInfo>
                      <DocName>{doc.name}</DocName>
                      <DocMeta>
                        <span>{doc.size}</span>
                        <span>•</span>
                        <span>Ajouté le {new Date(doc.uploadDate).toLocaleDateString('fr-FR')}</span>
                      </DocMeta>
                      {doc.status === 'rejected' && doc.rejectReason && (
                        <div style={{
                          marginTop: theme.spacing.sm,
                          padding: theme.spacing.sm,
                          background: theme.colors.error + '10',
                          borderRadius: theme.borderRadius.sm,
                          fontSize: theme.typography.sizes.xs,
                          color: theme.colors.error,
                          display: 'flex',
                          alignItems: 'center',
                          gap: theme.spacing.xs
                        }}>
                          <FiAlertCircle />
                          {doc.rejectReason}
                        </div>
                      )}
                    </DocInfo>
                    <StatusBadge status={doc.status}>
                      {getStatusIcon(doc.status)}
                      {getStatusText(doc.status)}
                    </StatusBadge>
                    <DocActions>
                      <IconButton title="Voir">
                        <FiEye />
                      </IconButton>
                      <IconButton title="Télécharger">
                        <FiDownload />
                      </IconButton>
                      <DeleteButton
                        title="Supprimer"
                        onClick={() => handleDeleteDocument(doc.id)}
                      >
                        <FiTrash2 />
                      </DeleteButton>
                    </DocActions>
                  </DocumentCard>
                ))}
              </DocumentList>
            ) : (
              <EmptyState>
                <FiFolder size={48} style={{ marginBottom: theme.spacing.md }} />
                <p>Aucun document dans cette catégorie</p>
              </EmptyState>
            )}
          </CategorySection>
        );
      })}
    </DashboardLayout>
  );
};
